import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Icon,
  HStack
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { QueryCategoryApi } from '../../../../apis/QueryMaintenance.api';
import {
  chooseDataStateType,
  MaintenanceModalDispLabels
} from '../../../molecules/MaintenanceModalDispLabels';
import { MaintenanceFooter } from '../MaintenanceFooter';
import {
  ArrowUpDownIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from '@chakra-ui/icons';
import { CategorySendMessageType } from '../../../constants/MaintenanceConstants';
import { CategorySearchCondition } from '../../../../pages/CategoryMaintenance';

const TableColumnEnum = {
  id: 'id',
  name: 'name',
  type: 'type',
  created_at: 'created_at',
  updated_at: 'updated_at'
};

export interface CategoryType {
  id: number;
  name: string;
  type: string;
  created_at: string;
  updated_at: string;
}

interface props {
  CategoryDatas: CategoryType[] | undefined;
  setMaintenanceDatas: React.Dispatch<
    React.SetStateAction<CategoryType[] | undefined>
  >;
  setChooseData: (data: chooseDataStateType) => void;
  onOpenBrowse: () => void;
  onOpenEdit: () => void;
  onToggle: () => void;
  Query: CategorySearchCondition;
  setQuery: React.Dispatch<React.SetStateAction<CategorySearchCondition>>;
  createOrUpdate: boolean;
  searchSwitch: boolean;
  chooseButton: string;
}

const headers = [
  { label: 'ID', key: 'id' },
  { label: 'カテゴリ名', key: 'name' },
  { label: 'タイプ', key: 'type' },
  { label: '登録日時', key: 'created_at' },
  { label: '更新日時', key: 'updated_at' }
];

const GetCsvFileName = () => {
  const date = new Date();
  const hour =
    date.getMonth().toString().length === 1
      ? '0' + (date.getMonth() + 1).toString()
      : date.getMonth().toString();
  const day =
    date.getDate().toString().length === 1
      ? '0' + date.getDate().toString()
      : date.getDate().toString();
  return 'categories-' + date.getFullYear() + '-' + hour + '-' + day;
};

const GetMessageTypeName = (messageType: string) => {
  switch (messageType) {
    case CategorySendMessageType.Signage:
      return '停留場メッセージ';
    case CategorySendMessageType.Obc:
      return 'OBCメッセージ';
    case CategorySendMessageType.Location:
      return '位置情報メッセージ';
    case CategorySendMessageType.Message:
      return '共通メッセージ';
    default:
      return '';
  }
};

export const MaintenanceCategoryTable: React.FC<props> = (props) => {
  const {
    CategoryDatas,
    setMaintenanceDatas,
    setChooseData,
    onOpenBrowse,
    onOpenEdit,
    onToggle,
    Query,
    setQuery,
    createOrUpdate,
    searchSwitch,
    chooseButton
  } = props;
  const [pageNow, setPageNow] = useState<number>(1);
  const [columnSortType, setColumnSortType] = useState<{
    key: string;
    sort: boolean;
  }>({ key: '', sort: false });
  const pageDispMax = 30;
  const pageMax = 6;
  const csvFileName = GetCsvFileName();
  const GetPageCount = (): number => {
    if (CategoryDatas !== undefined) {
      const divisionCount = Math.floor(CategoryDatas.length / pageDispMax);
      const remCount = CategoryDatas.length % pageDispMax === 0 ? 0 : 1;
      return divisionCount + remCount;
    }
    return 1;
  };

  const ColumnNameTriangleType = (column: string) => {
    if (columnSortType.key === column) {
      if (columnSortType.sort) {
        return TriangleUpIcon;
      } else {
        return TriangleDownIcon;
      }
    }
    return ArrowUpDownIcon;
  };

  const ColumnSortApi = async (column: string) => {
    const params = new URLSearchParams();
    const sortType = !columnSortType.sort;
    params.append('order', column + (sortType ? '_asc' : '_desc'));
    Query.categoryName !== '' &&
      params.append('name_contains', Query.categoryName);
    Query.type !== CategorySendMessageType.None &&
      params.append('type', Query.type);
    Query.dateCreatedFrom !== '' &&
      params.append('created_at_gte', Query.dateCreatedFrom);
    Query.dateCreatedTo !== '' &&
      params.append('created_at_lt', Query.dateCreatedTo);
    Query.dateUpdatedFrom !== '' &&
      params.append('updated_at_gte', Query.dateUpdatedFrom);
    Query.dateUpdatedTo !== '' &&
      params.append('updated_at_lt', Query.dateUpdatedTo);
    const query = '/?' + params.toString();

    setQuery((pre) => ({ ...pre, sort: { key: column, sortType: sortType } }));

    const response = await QueryCategoryApi(query);
    if (response.success && response.data !== null) {
      setMaintenanceDatas(response.data);
      setColumnSortType({ key: column, sort: sortType });
    } else {
      alert(column + 'のソートに失敗しました。');
    }
  };

  useEffect(() => {
    setPageNow(1);
  }, [searchSwitch, chooseButton]);

  useEffect(() => {
    setPageNow(1);
    setColumnSortType({ key: '', sort: false });
  }, [createOrUpdate]);

  return (
    <>
      <TableContainer
        position='absolute'
        h='100%'
        w='100%'
        ml={5}
        overflowY='auto'
        gridRow='2/3'
        gridColumn='1/2'
        overflowX='unset'
      >
        <Table variant='simple'>
          <Thead borderWidth='1px' position='sticky' top={0} zIndex='docked'>
            <Tr bg='gray.800'>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.id)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>ID</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.id)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.name)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>カテゴリ名</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.name)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.type)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>タイプ</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.type)}
                  />
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>登録日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>更新日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'></Th>
            </Tr>
          </Thead>
          <Tbody>
            {CategoryDatas !== undefined &&
              CategoryDatas.map((data, i) => {
                if (
                  pageDispMax * pageNow - pageDispMax <= i &&
                  i < pageDispMax * pageNow
                ) {
                  return (
                    <Tr key={`${'category_' + i}`}>
                      <Td>{data.id}</Td>
                      <Td>{data.name}</Td>
                      <Td>{GetMessageTypeName(data.type)}</Td>
                      <Td>{data.created_at}</Td>
                      <Td>{data.updated_at}</Td>
                      <Td>
                        <MaintenanceModalDispLabels
                          data={{ type: 'category', category: data }}
                          setChooseData={setChooseData}
                          onOpenBrowse={onOpenBrowse}
                          onOpenEdit={onOpenEdit}
                          onToggle={onToggle}
                        />
                      </Td>
                    </Tr>
                  );
                }
              })}
          </Tbody>
        </Table>
      </TableContainer>
      <MaintenanceFooter
        CsvData={CategoryDatas !== undefined ? CategoryDatas : []}
        DataLength={CategoryDatas?.length}
        Headers={headers}
        CsvFileName={csvFileName}
        GetPageCount={GetPageCount}
        pageMax={pageMax}
        pageNow={pageNow}
        setPageNow={setPageNow}
      />
    </>
  );
};
